import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Players } from '../api/client';
import { shortId } from '../utils/format';

interface UsernamesApi {
  /** uuid → username for everything resolved so far. */
  names: Record<string, string>;
  /** Display name for a uuid; falls back to a shortened uuid. */
  name: (uuid: string | null | undefined) => string;
  /** Ask for any of these uuids that are not known yet (batched). */
  ensure: (uuids: (string | null | undefined)[]) => void;
  /** Resolve a username to a uuid (cached, null when unknown). */
  lookupUuid: (username: string) => Promise<string | null>;
}

const UsernamesContext = createContext<UsernamesApi | null>(null);

/**
 * Small cache in front of POST /api/usernames and GET /api/player/uuid/:username.
 * Requests are batched per tick, and failures are remembered so we never hammer LuckPerms.
 */
export function UsernamesProvider({ children }: { children: React.ReactNode }) {
  const [names, setNames] = useState<Record<string, string>>({});
  const namesRef = useRef(names);
  namesRef.current = names;

  const pending = useRef(new Set<string>());
  const failed = useRef(new Set<string>());
  const queue = useRef(new Set<string>());
  const timer = useRef<number | null>(null);
  const uuidByName = useRef(new Map<string, string | null>());

  const flush = useCallback(async () => {
    timer.current = null;
    const uuids = Array.from(queue.current);
    queue.current.clear();
    if (uuids.length === 0) return;
    uuids.forEach((u) => pending.current.add(u));
    try {
      const result = await Players.usernames(uuids);
      setNames((prev) => ({ ...prev, ...result }));
      Object.entries(result).forEach(([uuid, username]) => uuidByName.current.set(username.toLowerCase(), uuid));
      uuids.forEach((u) => {
        if (!result[u]) failed.current.add(u);
      });
    } catch (e) {
      console.error('Failed to resolve usernames', e);
      uuids.forEach((u) => failed.current.add(u));
    } finally {
      uuids.forEach((u) => pending.current.delete(u));
    }
  }, []);

  const ensure = useCallback(
    (uuids: (string | null | undefined)[]) => {
      let added = false;
      uuids.forEach((u) => {
        if (!u) return;
        if (namesRef.current[u] || pending.current.has(u) || failed.current.has(u) || queue.current.has(u)) return;
        queue.current.add(u);
        added = true;
      });
      if (added && timer.current == null) {
        timer.current = window.setTimeout(flush, 30);
      }
    },
    [flush]
  );

  const name = useCallback((uuid: string | null | undefined) => (uuid ? namesRef.current[uuid] || shortId(uuid) : '-'), []);

  const lookupUuid = useCallback(async (username: string) => {
    const key = username.trim().toLowerCase();
    if (!key) return null;
    if (uuidByName.current.has(key)) return uuidByName.current.get(key) ?? null;
    const known = Object.entries(namesRef.current).find(([, n]) => n.toLowerCase() === key);
    if (known) return known[0];
    try {
      const uuid = await Players.uuidByUsername(username.trim());
      uuidByName.current.set(key, uuid);
      if (uuid) setNames((prev) => (prev[uuid] ? prev : { ...prev, [uuid]: username.trim() }));
      return uuid;
    } catch {
      uuidByName.current.set(key, null);
      return null;
    }
  }, []);

  useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    []
  );

  const value = useMemo<UsernamesApi>(() => ({ names, name, ensure, lookupUuid }), [names, name, ensure, lookupUuid]);
  return <UsernamesContext.Provider value={value}>{children}</UsernamesContext.Provider>;
}

export function useUsernames(): UsernamesApi {
  const ctx = useContext(UsernamesContext);
  if (!ctx) throw new Error('useUsernames must be used inside UsernamesProvider');
  return ctx;
}

/** Convenience: resolve a list of uuids and re-render as they arrive. */
export function useNamesFor(uuids: (string | null | undefined)[]): (uuid: string | null | undefined) => string {
  const { ensure, names } = useUsernames();
  const key = uuids.filter(Boolean).join('|');
  useEffect(() => {
    ensure(key ? key.split('|') : []);
  }, [key, ensure]);
  return useCallback((uuid) => (uuid ? names[uuid] || shortId(uuid) : '-'), [names]);
}
